import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Camera } from 'lucide-react';
import { axiosInstance } from '../App';
import ImageEditorModal from './ImageEditorModal';
import { getTodayIST, dateInputToISO } from '../utils/dateUtils';

export const MetalCreditModal = ({ isOpen, onClose, onSave, editData, base24kRate, gstInputStyle }) => {
  const [metalTypes, setMetalTypes] = useState([]);
  const [purityOptions, setPurityOptions] = useState([]);
  const [editingImage, setEditingImage] = useState(null);
  const [deposits, setDeposits] = useState([{
    id: Date.now(),
    type: '',
    type_name_other: '',
    item_name: '',
    gross_wt: '',
    less_wt: '',
    net_wt: '',
    purity: '',
    purity_label: '',
    fine_wt: '',
    reference: '',
    photos: [],
    date: getTodayIST()
  }]);

  // Load metal types and purity options on mount
  useEffect(() => {
    const loadMasters = async () => {
      try {
        // Load metal types
        const defaultTypes = [
          { id: 'cadbury', name: 'Cadbury', default_purity: 99.9 },
          { id: 'rava', name: 'Rava', default_purity: 99.5 },
          { id: 'old-gold', name: 'Old Gold (Jewellery)', default_purity: 90 },
          { id: 'akj-new', name: 'AKJ New', default_purity: 91.6 },
          { id: 'other', name: 'Other', default_purity: 90 }
        ];
        setMetalTypes(defaultTypes);

        // Load purity options (same as order items)
        const purities = [
          { value: 100, label: '24K', percent: 100 },
          { value: 91.6, label: '22K (916)', percent: 91.6 },
          { value: 75, label: '18K', percent: 75 }
        ];
        setPurityOptions(purities);
      } catch (err) {
        console.error('Error loading masters:', err);
      }
    };
    if (isOpen) {
      loadMasters();
    }
  }, [isOpen]);

  // Pre-fill if editing
  useEffect(() => {
    if (editData && editData.deposits) {
      const loadedDeposits = editData.deposits.map((dep, idx) => ({
        id: Date.now() + idx,
        ...dep
      }));
      setDeposits(loadedDeposits);
    } else if (!editData) {
      // Reset to default
      setDeposits([{
        id: Date.now(),
        type: '',
        type_name_other: '',
        item_name: '',
        gross_wt: '',
        less_wt: '',
        net_wt: '',
        purity: '',
        purity_label: '',
        fine_wt: '',
        reference: '',
        photos: [],
        date: getTodayIST()
      }]);
    }
  }, [editData, isOpen]);

  if (!isOpen) return null;

  const addDeposit = () => {
    setDeposits([...deposits, {
      id: Date.now(),
      type: '',
      type_name_other: '',
      item_name: '',
      gross_wt: '',
      less_wt: '',
      net_wt: '',
      purity: '',
      purity_label: '',
      fine_wt: '',
      reference: '',
      photos: [],
      date: getTodayIST()
    }]);
  };

  const removeDeposit = (id) => {
    if (deposits.length > 1) {
      setDeposits(deposits.filter(dep => dep.id !== id));
    }
  };

  const updateDeposit = (id, field, value) => {
    setDeposits(deposits.map(dep => {
      if (dep.id !== id) return dep;
      
      const updated = { ...dep, [field]: value };
      
      // Auto-set purity based on type
      if (field === 'type') {
        const selectedType = metalTypes.find(t => t.id === value);
        if (selectedType && selectedType.id !== 'other' && selectedType.id !== 'akj-new') {
          updated.purity = selectedType.default_purity.toString();
        }
        // For AKJ New, set default to 22K
        if (selectedType && selectedType.id === 'akj-new') {
          updated.purity = '91.6';
          updated.purity_label = '22K (916)';
        }
      }
      
      // Handle purity selection for AKJ New
      if (field === 'purity' && dep.type === 'akj-new') {
        const selected = purityOptions.find(p => p.percent.toString() === value);
        if (selected) {
          updated.purity_label = selected.label;
        }
      }
      
      // Auto-calculate Net Wt from Gross - Less
      if (field === 'gross_wt' || field === 'less_wt') {
        const gross = parseFloat(updated.gross_wt) || 0;
        const less = parseFloat(updated.less_wt) || 0;
        updated.net_wt = Math.max(0, gross - less).toFixed(3);
      }
      
      // Auto-calculate Fine Wt from Net × Purity%
      if (field === 'net_wt' || field === 'purity' || field === 'gross_wt' || field === 'less_wt') {
        const net = parseFloat(updated.net_wt) || 0;
        const purity = parseFloat(updated.purity) || 0;
        updated.fine_wt = (net * purity / 100).toFixed(3);
      }
      
      return updated;
    }));
  };

  const handlePhotoUpload = (id, event) => {
    // Prevent any default behavior and event bubbling
    event.preventDefault();
    event.stopPropagation();
    
    const file = event.target.files[0];
    if (!file) {
      event.target.value = '';
      return;
    }

    const deposit = deposits.find(d => d.id === id);
    if (deposit && deposit.photos.length >= 3) {
      alert('Maximum 3 photos allowed');
      event.target.value = '';
      return;
    }

    // Read file and open image editor
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditingImage({ depositId: id, imageData: reader.result });
    };
    reader.onerror = () => {
      alert('Failed to read image file');
      event.target.value = '';
    };
    reader.readAsDataURL(file);
    
    // Clear the input so the same file can be selected again
    event.target.value = '';
  };

  const handleImageSaved = (depositId, imageData) => {
    const deposit = deposits.find(d => d.id === depositId);
    if (deposit) {
      // Store both original and edited images for audit trail
      // Structure: { original: base64, edited: base64, timestamp: ISO }
      const imageEntry = {
        original: imageData.original || imageData.edited || '',
        edited: imageData.edited || imageData.original || '',
        timestamp: new Date().toISOString()
      };
      
      // Validate that we have at least one valid image
      if (!imageEntry.original && !imageEntry.edited) {
        console.error('Image save failed: no valid image data', imageData);
        alert('Failed to save image. Please try again.');
        return;
      }
      
      updateDeposit(depositId, 'photos', [...deposit.photos, imageEntry]);
    }
    setEditingImage(null);
  };

  const removePhoto = (depositId, photoIndex) => {
    const deposit = deposits.find(d => d.id === depositId);
    const newPhotos = deposit.photos.filter((_, idx) => idx !== photoIndex);
    updateDeposit(depositId, 'photos', newPhotos);
  };

  const getFieldsForType = (typeId) => {
    switch (typeId) {
      case 'cadbury':
      case 'rava':
        return {
          showGrossLess: false,
          showItemName: false,
          showNetWt: true,
          showPurity: true,
          showPhotos: false,
          purityInput: 'text'
        };
      case 'old-gold':
        return {
          showGrossLess: true,
          showItemName: true,
          showNetWt: true,
          showPurity: true,
          showPhotos: false,
          purityInput: 'text'
        };
      case 'akj-new':
        return {
          showGrossLess: true,
          showItemName: true,
          showNetWt: true,
          showPurity: true,
          showPhotos: true,
          purityInput: 'selector'
        };
      case 'other':
        return {
          showGrossLess: true,
          showItemName: true,
          showNetWt: true,
          showPurity: true,
          showPhotos: false,
          purityInput: 'text'
        };
      default:
        return {
          showGrossLess: false,
          showItemName: false,
          showNetWt: false,
          showPurity: false,
          showPhotos: false,
          purityInput: 'text'
        };
    }
  };

  const getTotals = () => {
    const totalFine = deposits.reduce((sum, dep) => sum + (parseFloat(dep.fine_wt) || 0), 0);
    return { totalFine: totalFine.toFixed(3) };
  };

  const handleSave = () => {
    const { totalFine } = getTotals();
    if (!totalFine || parseFloat(totalFine) === 0) return;
    
    // Validate AKJ New mandatory fields
    for (const dep of deposits) {
      if (dep.type === 'akj-new' && !dep.item_name) {
        alert('Item Name is mandatory for AKJ New');
        return;
      }
    }
    
    // Calculate advance equivalent value
    const advanceEquivalent = parseFloat(totalFine) * (parseFloat(base24kRate) || 0);
    
    onSave({
      mode: 'Metal Credit',
      fine_wt: parseFloat(totalFine),
      advance_equivalent: advanceEquivalent,
      deposits: deposits.map(({ id, ...dep }) => dep),
      date: dateInputToISO(deposits[0]?.date || getTodayIST()),
      timestamp: editData?.timestamp || dateInputToISO(deposits[0]?.date || getTodayIST())
    });
    onClose();
  };

  const { totalFine } = getTotals();

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-t-3xl w-full max-w-md p-6 space-y-4 animate-slide-up max-h-[85vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">✨ {editData ? 'Edit ' : ''}Fine Metal Deposit</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-blue-50 p-3 rounded-lg text-xs text-blue-800">
          <strong>Note:</strong> This adds metal credit (grams) to count toward 80% advance requirement.
        </div>

        <div className="bg-amber-50 p-3 rounded-lg">
          <div className="text-sm font-semibold text-amber-800">
            Total Fine: {totalFine}g
            {base24kRate && (
              <span className="ml-2 text-xs">
                (≈ ₹{(parseFloat(totalFine) * parseFloat(base24kRate)).toLocaleString('en-IN', {maximumFractionDigits: 0})})
              </span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {deposits.map((deposit, idx) => {
            const fields = getFieldsForType(deposit.type);
            
            return (
              <div key={deposit.id} className="border-2 border-amber-200 rounded-lg p-3 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold text-amber-700">Deposit #{idx + 1}</span>
                  {deposits.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDeposit(deposit.id)}
                      className="text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Type Selection */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-2">Type *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {metalTypes.map(type => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => updateDeposit(deposit.id, 'type', type.id)}
                        className={`py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                          deposit.type === type.id
                            ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                        }`}
                      >
                        {type.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Other Type Name */}
                {deposit.type === 'other' && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Type Name *</label>
                    <input
                      type="text"
                      className="input-field w-full text-sm"
                      value={deposit.type_name_other}
                      onChange={(e) => updateDeposit(deposit.id, 'type_name_other', e.target.value)}
                      placeholder="Enter metal type"
                    />
                  </div>
                )}

                {/* Item Name */}
                {fields.showItemName && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Item Name {deposit.type === 'akj-new' ? '*' : '(optional)'}
                    </label>
                    <input
                      type="text"
                      className="input-field w-full text-sm"
                      value={deposit.item_name}
                      onChange={(e) => updateDeposit(deposit.id, 'item_name', e.target.value)}
                      placeholder="e.g., Ring, Bangle"
                    />
                  </div>
                )}

                {/* Row 1: Gross, Less, Net Weight (3 columns) - For types with Gross/Less */}
                {fields.showGrossLess && (
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Gross Wt (g) *</label>
                      <input
                        type="number"
                        step="0.001"
                        className="input-field w-full text-sm"
                        value={deposit.gross_wt}
                        onChange={(e) => updateDeposit(deposit.id, 'gross_wt', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Less Wt (g)</label>
                      <input
                        type="number"
                        step="0.001"
                        className="input-field w-full text-sm"
                        value={deposit.less_wt}
                        onChange={(e) => updateDeposit(deposit.id, 'less_wt', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Net Wt (g) *</label>
                      <input
                        type="number"
                        step="0.001"
                        className="input-field w-full text-sm bg-gray-50"
                        value={deposit.net_wt}
                        onChange={(e) => updateDeposit(deposit.id, 'net_wt', e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {/* Net Weight only (for Cadbury/Rava) */}
                {!fields.showGrossLess && fields.showNetWt && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Net Wt (g) *</label>
                    <input
                      type="number"
                      step="0.001"
                      className="input-field w-full text-sm"
                      value={deposit.net_wt}
                      onChange={(e) => updateDeposit(deposit.id, 'net_wt', e.target.value)}
                    />
                  </div>
                )}

                {/* Row 2: Purity & Fine Weight (2 columns) */}
                {fields.showPurity && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Purity *</label>
                      {fields.purityInput === 'selector' ? (
                        <select
                          className="input-field w-full text-sm"
                          value={deposit.purity}
                          onChange={(e) => updateDeposit(deposit.id, 'purity', e.target.value)}
                        >
                          <option value="">Select</option>
                          {purityOptions.map(opt => (
                            <option key={opt.value} value={opt.percent}>{opt.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="number"
                          step="0.01"
                          className="input-field w-full text-sm"
                          value={deposit.purity}
                          onChange={(e) => updateDeposit(deposit.id, 'purity', e.target.value)}
                          placeholder="e.g., 99.9"
                        />
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Fine Wt (g) *</label>
                      <input
                        type="number"
                        step="0.001"
                        className="input-field w-full text-sm bg-green-50 font-semibold text-green-700"
                        value={deposit.fine_wt}
                        onChange={(e) => updateDeposit(deposit.id, 'fine_wt', e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {/* Photos (AKJ New only) */}
                {fields.showPhotos && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Photos (up to 3)</label>
                    <div className="flex gap-2 flex-wrap">
                      {deposit.photos.map((photo, photoIdx) => {
                        // Handle both old format (string) and new format (object with original/edited)
                        let displayImage = '';
                        
                        if (typeof photo === 'string') {
                          // Old format - direct string URL or base64
                          displayImage = photo;
                        } else if (photo && typeof photo === 'object') {
                          // New format - object with original/edited properties
                          // Prefer edited version, fallback to original
                          displayImage = photo.edited || photo.original || '';
                        }
                        
                        if (!displayImage) {
                          console.warn('Photo missing both edited and original:', photo);
                          return null;
                        }
                        
                        return (
                          <div key={photoIdx} className="relative">
                            <img 
                              src={displayImage} 
                              alt={`Photo ${photoIdx + 1}`} 
                              className="w-16 h-16 object-cover rounded border-2 border-amber-200"
                              onError={(e) => {
                                console.error('Image load error for:', {
                                  photoIdx,
                                  depositId: deposit.id,
                                  photoType: typeof photo,
                                  hasEdited: !!(typeof photo === 'object' && photo.edited),
                                  hasOriginal: !!(typeof photo === 'object' && photo.original),
                                  urlPrefix: displayImage?.substring(0, 30)
                                });
                                // Show a placeholder with better visibility
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-size="12"%3EImage%3C/text%3E%3C/svg%3E';
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(deposit.id, photoIdx)}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                      {deposit.photos.length < 3 && (
                        <label 
                          className="w-16 h-16 border-2 border-dashed border-amber-300 rounded flex items-center justify-center cursor-pointer hover:bg-amber-50"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Camera className="w-6 h-6 text-amber-600" />
                          <input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/heic,image/heif"
                            className="hidden"
                            onChange={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handlePhotoUpload(deposit.id, e);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                )}

                {/* Reference/Note */}
                {deposit.type && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Reference/Note (optional)</label>
                    <input
                      type="text"
                      className="input-field w-full text-sm"
                      value={deposit.reference}
                      onChange={(e) => updateDeposit(deposit.id, 'reference', e.target.value)}
                      placeholder="Reference number or note"
                    />
                  </div>
                )}

                {/* Date */}
                {deposit.type && (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Date *</label>
                    <input
                      type="date"
                      className="input-field w-full text-sm"
                      value={deposit.date}
                      onChange={(e) => updateDeposit(deposit.id, 'date', e.target.value)}
                    />
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button"
            onClick={addDeposit}
            className="w-full py-2 border-2 border-dashed border-amber-300 rounded-lg text-amber-600 font-semibold text-sm flex items-center justify-center gap-1"
          >
            <Plus className="w-4 h-4" />
            Add Another Deposit
          </button>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={parseFloat(totalFine) === 0}
            className="flex-1 py-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white rounded-lg font-semibold disabled:opacity-50"
          >
            {editData ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
      </div>
      
      {/* Image Editor Modal - Rendered outside at higher z-index */}
      {editingImage && (
        <ImageEditorModal
          imageData={editingImage.imageData}
          onClose={() => setEditingImage(null)}
          onSave={(editedImage) => {
            handleImageSaved(editingImage.depositId, editedImage);
            setEditingImage(null);
          }}
          returnBothImages={true}
        />
      )}
    </>
  );
};
